import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/shared/utils/cn';

import { Icon, type IconName } from './Icon';

/**
 * R-050 Toast (DEC-08) — confirms that an action completed.
 *
 * ── What a toast is for, and what it is not ────────────────────────────────
 * A short, non-critical confirmation that goes away by itself. Anything a
 * person must act on — a refused form, a financial or security problem — is
 * an Alert or a dialog that stays until it is dealt with, never a toast. The
 * committed workflow results stay on their records; ordinary preference saves
 * and invitation resends use this transient confirmation.
 *
 * ── One announcement, not two ───────────────────────────────────────────────
 * On its own, a Toast is a `status` region. Inside a ToastRegion it is not: the
 * region is the one live region that exists before any toast arrives, which
 * is what makes a screen reader announce each toast once and reliably.
 */
export type ToastTone = 'default' | 'exception' | 'success' | 'info' | 'warning' | 'error';

const TONE_ICONS: Record<ToastTone, IconName> = {
  default: 'confirm',
  exception: 'alert',
  success: 'confirm',
  info: 'alert',
  warning: 'warning',
  error: 'alert',
};

export const MAX_VISIBLE_TOASTS = 3;

export function Toast({
  title,
  text,
  tone = 'default',
  icon,
  onClose,
  announce = true,
}: {
  title: ReactNode;
  text?: ReactNode;
  /** `exception` adds the Orange leading rule; for a case where a person must look. */
  tone?: ToastTone;
  icon?: IconName;
  onClose?: () => void;
  /** False inside a ToastRegion, which announces for it. */
  announce?: boolean;
}) {
  const { t } = useTranslation();

  return (
    <div
      role={announce ? 'status' : undefined}
      className={cn(
        'type-ui flex w-full items-start gap-3 rounded-(--radius-card) bg-(--fid-plum-deep) px-4 py-3 text-ink-on-brand shadow-(--shadow-lg)',
        'sm:w-auto sm:min-w-[300px] sm:max-w-[400px]',
        tone === 'exception' && 'border-s-[3px] border-(--action-exception)',
        tone === 'warning' && 'border-s-[3px] border-warning-line',
        tone === 'error' && 'border-s-[3px] border-critical-line',
      )}
    >
      <Icon
        name={icon ?? TONE_ICONS[tone]}
        className={cn(
          'mt-0.5 shrink-0',
          tone === 'exception' ? 'text-(--action-exception)' : 'text-ink-on-brand',
        )}
      />
      <div className="min-w-0 flex-1">
        <p className="font-semibold wrap-anywhere">{title}</p>
        {text ? <p className="mt-0.5 text-(--fid-plum-200) wrap-anywhere">{text}</p> : null}
      </div>
      {onClose ? (
        <button
          type="button"
          onClick={onClose}
          aria-label={t('app.dismiss')}
          className={cn(
            '-my-2 -me-2 inline-flex min-h-11 min-w-11 flex-none items-center justify-center rounded-sm text-(--fid-plum-200)',
            'hover:text-ink-on-brand focus-visible:outline-2 focus-visible:outline-focus',
          )}
        >
          <Icon name="close" size={16} />
        </button>
      ) : null}
    </div>
  );
}

export interface ToastItem {
  id: string;
  title: ReactNode;
  text?: ReactNode;
  tone?: ToastTone;
  icon?: IconName;
}

/**
 * Where toasts appear: the bottom-start of the working area. On a phone they sit
 * above the bottom navigation AND the pinned action bar, so a toast never covers
 * the action a person is about to take.
 *
 * Each toast leaves on its own after `duration` (UI UX Pro Max: 3–5 seconds for
 * transient, non-critical feedback), and waits while the pointer or keyboard
 * focus is on it (WCAG 2.2.1), so it cannot vanish mid-read. An `exception`
 * toast stays until it is dismissed.
 */
export function ToastRegion({
  toasts,
  onDismiss,
  duration = 5000,
}: {
  toasts: ToastItem[];
  onDismiss: (id: string) => void;
  duration?: number;
}) {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="false"
      className={cn(
        'pointer-events-none fixed z-40 flex max-h-[50dvh] flex-col gap-2 overflow-y-auto',
        'inset-x-4 bottom-[calc(var(--layout-mobile-bottomnav-h)+var(--layout-mobile-actionbar-h)+env(safe-area-inset-bottom)+0.5rem)]',
        'lg:inset-x-auto lg:start-6 lg:bottom-6',
      )}
    >
      {toasts.slice(0, MAX_VISIBLE_TOASTS).map((toast) => (
        <TimedToast key={toast.id} toast={toast} onDismiss={onDismiss} duration={duration} />
      ))}
    </div>
  );
}

function TimedToast({
  toast,
  onDismiss,
  duration,
}: {
  toast: ToastItem;
  onDismiss: (id: string) => void;
  duration: number;
}) {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const held = hovered || focused;
  const remaining = useRef(duration);

  useEffect(() => {
    if (toast.tone === 'exception' || toast.tone === 'warning' || toast.tone === 'error' || held)
      return;

    const started = Date.now();
    const timer = window.setTimeout(() => onDismiss(toast.id), remaining.current);

    return () => {
      window.clearTimeout(timer);
      // Resume later from where it stopped, not from the start.
      remaining.current = Math.max(0, remaining.current - (Date.now() - started));
    };
  }, [held, toast.id, toast.tone, onDismiss]);

  return (
    <div
      className="pointer-events-auto"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false);
      }}
    >
      <Toast
        title={toast.title}
        text={toast.text}
        tone={toast.tone}
        icon={toast.icon}
        announce={false}
        onClose={() => onDismiss(toast.id)}
      />
    </div>
  );
}
